/**
 * Pure helper for classifying a training-expiry date into one of four
 * states used by the Investigators list + detail expiry indicators.
 *
 *   "expired"  — date is in the past
 *   "expiring" — date is within the next 30 days
 *   "current"  — date is more than 30 days away
 *   "none"     — date is null (never recorded)
 */
export type ExpiryState = "expired" | "expiring" | "current" | "none";

const EXPIRING_WINDOW_DAYS = 30;

export function classifyExpiry(iso: string | null): ExpiryState {
  if (!iso) return "none";
  const expiry = new Date(iso);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  if (diffMs < 0) return "expired";
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays <= EXPIRING_WINDOW_DAYS) return "expiring";
  return "current";
}

/**
 * MUI Chip color mapping for the four states. "default" is used for
 * "none" since the absence of a date is informational, not alarming.
 */
export const EXPIRY_STATE_CHIP_COLOR: Record<
  ExpiryState,
  "error" | "warning" | "success" | "default"
> = {
  expired: "error",
  expiring: "warning",
  current: "success",
  none: "default",
};
